import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { FileText, Download, Clock, CheckCircle, ArrowLeft, ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from '@/components/ui/dialog';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

const StatusCorrectionPassport = () => {
  const navigate = useNavigate();
  const [step2Open, setStep2Open] = useState(false);
  const [step3Open, setStep3Open] = useState(false);
  const [step4Open, setStep4Open] = useState(false);
  const [step5Open, setStep5Open] = useState(false);

  return (
    <div className="min-h-screen">
      <Header />
      
      <section className="pt-32 pb-20 bg-background">
        <div className="max-w-4xl mx-auto px-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/diy-processes')}
            className="mb-8 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to DIY Processes
          </Button>

          <div className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="p-4 rounded-xl bg-primary/10">
                <FileText className="h-12 w-12 text-primary" />
              </div>
            </div>
            <h1 className="font-rockwell text-4xl md:text-5xl font-semibold text-primary mb-6">
              Correct your Political Status via DS-11 Passport Application
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
              Learn how to correct your status when applying for a passport. 
              Understand the difference between national and citizen classifications.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-card border border-border rounded-lg p-6 text-center">
              <Clock className="h-8 w-8 text-primary mx-auto mb-3" />
              <h3 className="font-semibold text-foreground mb-2">Time Estimate</h3>
              <p className="text-muted-foreground">instant</p>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-6 text-center">
              <Download className="h-8 w-8 text-primary mx-auto mb-3" />
              <h3 className="font-semibold text-foreground mb-2">Templates</h3>
              <p className="text-muted-foreground">3 templates included</p>
            </div>
          </div>

          <div className="bg-card border border-border rounded-xl p-8 mb-8">
            <h2 className="font-rockwell text-2xl font-semibold text-foreground mb-6">
              What You'll Learn
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-foreground mb-3">Essential Knowledge</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-1 flex-shrink-0" />
                    National vs citizen status distinctions
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-1 flex-shrink-0" />
                    Constitutional status options and rights
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-1 flex-shrink-0" />
                    Correct passport application procedure
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-3">Practical Skills</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-1 flex-shrink-0" />
                    Form completion and submission
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-1 flex-shrink-0" />
                    Supporting documentation preparation
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-1 flex-shrink-0" />
                    One day Passport approval
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="text-center mb-8">
            <h2 className="font-rockwell text-2xl font-semibold text-foreground underline">
              Start below
            </h2>
          </div>

          {/* Step Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Step 1 */}
            <Dialog>
              <DialogTrigger asChild>
                <div className="group cursor-pointer">
                  <div className="bg-card border border-border rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-primary/50 min-h-[160px] flex flex-col justify-between">
                    <div>
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 mb-4">
                        <span className="text-xl font-bold text-primary">1</span>
                      </div>
                      <h3 className="font-rockwell text-lg font-semibold text-foreground mb-3 leading-tight">
                        Understanding U.S. citizen v. Constitutional American National
                      </h3>
                    </div>
                    <Button variant="outline" size="sm" className="w-full mt-4 group-hover:border-primary/50 transition-colors">
                      Start Step 1
                    </Button>
                  </div>
                </div>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] md:max-h-[85vh] overflow-y-auto mx-2 md:mx-8 my-2 md:my-4 w-[95vw] md:w-auto [&>button]:hidden">
                <DialogHeader className="relative pb-4 border-b border-border/20">
                  <DialogClose asChild>
                    <Button
                      variant="outline"
                      className="absolute right-0 top-0 h-12 w-20 rounded-lg bg-background/95 hover:bg-primary/5 hover:text-primary transition-all duration-300 hover:scale-[1.02] border-2 border-border/40 hover:border-primary/40 shadow-sm hover:shadow-md backdrop-blur-sm group"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1.5 group-hover:-translate-x-0.5 transition-transform duration-200" />
                      <span className="text-sm font-semibold">Back</span>
                    </Button>
                  </DialogClose>
                  <DialogTitle className="text-lg md:text-2xl font-bold text-primary leading-tight pr-24">
                    Step 1: Understanding U.S. citizen v. Constitutional American National
                  </DialogTitle>
                </DialogHeader>
                <div className="text-foreground mt-4 md:mt-6 text-sm md:text-base">
                  <div className="space-y-6 md:space-y-8">
                    <div className="prose prose-lg max-w-none">
                      <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                        There are two radically different statuses you can adopt as an American that sound similar—but place you in completely different jurisdictions:
                      </p>
                      
                      <ul className="list-disc list-inside mb-6 text-muted-foreground space-y-2">
                        <li><strong>"US citizen"</strong> under the 14th Amendment</li>
                        <li><strong>"American national"</strong> of the United States of America Republic, also known as a constitutional American</li>
                      </ul>
                      
                      <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                        Most of us were never told what a US Citizen actually is. But once you know—you must choose.
                      </p>

                      <div className="mb-8">
                        <h3 className="font-rockwell text-xl font-semibold text-primary mb-4">
                          1. 14th Amendment "U.S. Citizen" – A Subject of the Corporate State
                        </h3>
                        
                        <p className="text-muted-foreground mb-4">
                          A U.S. citizen, as defined by the 14th Amendment, is not what you think it is.
                        </p>
                        
                        <p className="text-muted-foreground mb-4">
                          It is not someone born in the land of the united States of America and protected by common law. It is a federal status, created after the Civil War, and it places you under a commercial, administrative jurisdiction—not constitutional, not common law, not natural law.
                        </p>
                        
                        <p className="text-muted-foreground mb-4">When you accept or presume this status, you:</p>
                        
                        <ul className="list-disc list-inside mb-4 text-muted-foreground space-y-2 ml-4">
                          <li>Become a subject of Congress, not a sovereign of the land.</li>
                          <li>Are governed by public policy, not natural rights.</li>
                          <li>Are treated as a corporate entity—a legal fiction, not a living man or woman.</li>
                          <li>Are presumed to have voluntarily waived your God-given rights in exchange for government privileges and benefits.</li>
                        </ul>
                        
                        <p className="text-muted-foreground mb-6 font-semibold">
                          This is the hidden contract. It is voluntary, yet it's presumed unless you rebut it.
                        </p>
                      </div>

                      <div className="mb-8">
                        <h3 className="font-rockwell text-xl font-semibold text-primary mb-4">
                          2. American National – Standing on the Land in Full Capacity
                        </h3>
                        
                        <p className="text-muted-foreground mb-4">
                          An American national of the united States of America (note the lowercase "u") is a man or woman born on the land of the republic—not a subject of the federal corporation.
                        </p>
                        
                        <p className="text-muted-foreground mb-4">As an American national:</p>
                        
                        <ul className="list-disc list-inside mb-4 text-muted-foreground space-y-2 ml-4">
                          <li>You are governed by God's law, Natural Law, and the original Constitution.</li>
                          <li>You do not fall under federal territorial jurisdiction, unless you consent.</li>
                          <li>You retain all unalienable rights, including life, liberty, property, travel, and self-governance.</li>
                          <li>You correct the record to reflect your true status—and you live free from presumptive contracts and obligations created by legal fiction.</li>
                        </ul>
                        
                        <div className="bg-primary/5 p-6 rounded-lg border border-primary/10 mb-6">
                          <p className="text-muted-foreground font-semibold text-center">
                            American nationals are the creditors.<br/>
                            14th Amendment citizens are the debtors.<br/>
                            American nationals are trustees of the republic.<br/>
                            U.S. citizens are beneficiaries of the state—and in law, beneficiaries are controlled.
                          </p>
                        </div>
                      </div>

                      <div className="mb-8">
                        <h3 className="font-rockwell text-xl font-semibold text-primary mb-4">
                          The Serious Implications:
                        </h3>
                        
                        <div className="grid md:grid-cols-2 gap-6 mb-6">
                          <div className="bg-red-50 p-6 rounded-lg border border-red-200">
                            <h4 className="font-semibold text-red-800 mb-3">If you operate as a 14th Amendment U.S. citizen:</h4>
                            <ul className="list-disc list-inside text-red-700 space-y-2 text-sm">
                              <li>You are subject to IRS codes, administrative courts, and federal regulations.</li>
                              <li>You can be taxed without direct apportionment, summoned, fined, and treated as an asset of the U.S. Corporation.</li>
                              <li>Your children, land, and labor are presumed collateral in the national debt system.</li>
                            </ul>
                          </div>
                          
                          <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                            <h4 className="font-semibold text-green-800 mb-3">If you stand as an American national:</h4>
                            <ul className="list-disc list-inside text-green-700 space-y-2 text-sm">
                              <li>You are a private man/woman, outside commercial jurisdiction.</li>
                              <li>You are protected by the law of the land, not statutes made for franchises.</li>
                              <li>You can reserve your rights, control your property, and operate freely—as long as you cause no harm.</li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      <div className="mb-8">
                        <h3 className="font-rockwell text-xl font-semibold text-primary mb-4">
                          This Is Not Theory. It's Jurisdictional Reality.
                        </h3>
                        
                        <p className="text-muted-foreground mb-4">
                          The difference isn't just philosophical—it's jurisdictional, legal, and spiritually binding.
                        </p>
                        
                        <p className="text-muted-foreground mb-4">
                          Every time you sign a government form, take a benefit, or use a title (like "U.S. citizen"), you're presumed to be in contract. But you can correct your status, declare your truth, record your standing, and live as the free man or woman you were born to be.
                        </p>
                        
                        <div className="bg-primary/5 p-6 rounded-lg border border-primary/10 text-center">
                          <p className="text-muted-foreground mb-2"><strong>The choice is yours:</strong></p>
                          <p className="text-muted-foreground mb-2">Be a citizen-slave under federal franchise...</p>
                          <p className="text-muted-foreground mb-4"><strong>Or be a free national of the republic, standing under God.</strong></p>
                          <p className="text-primary font-semibold">This is about restoring your name, your standing, and your inheritance.</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 md:p-6 mt-6">
                    <h4 className="font-bold text-foreground text-base md:text-lg mb-3 md:mb-4">⏰ Time for Action</h4>
                    <p className="text-muted-foreground">
                      Explain to yourself without going through notes or reading anything, the difference between a U.S. citizen and a constitutional American national.
                    </p>
                  </div>

                  <div className="mt-6 text-center">
                    <Button 
                      onClick={() => setStep2Open(true)}
                      className="bg-primary hover:bg-primary/90 text-primary-foreground"
                    >
                      Go to Step 2
                    </Button>
                  </div>

                  <div className="h-16"></div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Step 2 */}
            <Dialog open={step2Open} onOpenChange={setStep2Open}>
              <DialogTrigger asChild>
                <div className="group cursor-pointer">
                  <div className="bg-card border border-border rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-primary/50 min-h-[160px] flex flex-col justify-between">
                    <div>
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 mb-4">
                        <span className="text-xl font-bold text-primary">2</span>
                      </div>
                      <h3 className="font-rockwell text-lg font-semibold text-foreground mb-3 leading-tight">
                        Reading Instructions for DS-11 passport Application
                      </h3>
                    </div>
                    <Button variant="outline" size="sm" className="w-full mt-4 group-hover:border-primary/50 transition-colors">
                      Start Step 2
                    </Button>
                  </div>
                </div>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] md:max-h-[85vh] overflow-y-auto mx-2 md:mx-8 my-2 md:my-4 w-[95vw] md:w-auto [&>button]:hidden">
                <DialogHeader className="relative pb-4 border-b border-border/20">
                  <DialogClose asChild>
                    <Button
                      variant="outline"
                      className="absolute right-0 top-0 h-12 w-20 rounded-lg bg-background/95 hover:bg-primary/5 hover:text-primary transition-all duration-300 hover:scale-[1.02] border-2 border-border/40 hover:border-primary/40 shadow-sm hover:shadow-md backdrop-blur-sm group"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1.5 group-hover:-translate-x-0.5 transition-transform duration-200" />
                      <span className="text-sm font-semibold">Back</span>
                    </Button>
                  </DialogClose>
                  <DialogTitle className="text-lg md:text-2xl font-bold text-primary leading-tight pr-24">
                    Step 2: Reading Instructions for DS-11 passport Application
                  </DialogTitle>
                </DialogHeader>
                <div className="text-foreground mt-4 md:mt-6 text-sm md:text-base">
                  <div className="space-y-6 md:space-y-8">
                    <div className="space-y-4">
                      <h3 className="text-xl font-semibold text-foreground">Read the Entire DS-11 Application</h3>
                      
                      <div className="bg-muted/50 border border-border rounded-lg p-4">
                        <p className="text-foreground font-medium mb-3">Do not fill anything out yet.</p>
                        
                        <p className="text-foreground mb-4">
                          Your only task right now is to read every single word of the DS-11 passport application and its instructions. 
                          No skipping. No scanning. Take your time and absorb it all—line by line.
                        </p>
                        
                        <p className="text-foreground mb-3">Understanding comes before action.</p>
                        <p className="text-foreground">We'll break it down together in the next step.</p>
                      </div>

                      <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                        <h4 className="font-semibold text-amber-800 dark:text-amber-200 mb-2">Time for Action:</h4>
                        <p className="text-amber-700 dark:text-amber-300">
                          Read the entire DS-11 passport application form and its instructions thoroughly, 
                          word by word, without filling anything out.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 text-center">
                    <Button 
                      onClick={() => setStep3Open(true)}
                      className="bg-primary hover:bg-primary/90 text-primary-foreground"
                    >
                      Go to Step 3
                    </Button>
                  </div>

                  <div className="h-16"></div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Step 3 */}
            <Dialog open={step3Open} onOpenChange={setStep3Open}>
              <DialogTrigger asChild>
                <div className="group cursor-pointer">
                  <div className="bg-card border border-border rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-primary/50 min-h-[160px] flex flex-col justify-between">
                    <div>
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 mb-4">
                        <span className="text-xl font-bold text-primary">3</span>
                      </div>
                      <h3 className="font-rockwell text-lg font-semibold text-foreground mb-3 leading-tight">
                        Filling out the DS-11 Passport Application
                      </h3>
                    </div>
                    <Button variant="outline" size="sm" className="w-full mt-4 group-hover:border-primary/50 transition-colors">
                      Start Step 3
                    </Button>
                  </div>
                </div>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] md:max-h-[85vh] overflow-y-auto mx-2 md:mx-8 my-2 md:my-4 w-[95vw] md:w-auto [&>button]:hidden">
                <DialogHeader className="relative pb-4 border-b border-border/20">
                  <DialogClose asChild>
                    <Button
                      variant="outline"
                      className="absolute right-0 top-0 h-12 w-20 rounded-lg bg-background/95 hover:bg-primary/5 hover:text-primary transition-all duration-300 hover:scale-[1.02] border-2 border-border/40 hover:border-primary/40 shadow-sm hover:shadow-md backdrop-blur-sm group"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1.5 group-hover:-translate-x-0.5 transition-transform duration-200" />
                      <span className="text-sm font-semibold">Back</span>
                    </Button>
                  </DialogClose>
                  <DialogTitle className="text-lg md:text-2xl font-bold text-primary leading-tight pr-24">
                    Step 3: Filling out the DS-11 Passport Application
                  </DialogTitle>
                </DialogHeader>
                <div className="text-foreground mt-4 md:mt-6 text-sm md:text-base">
                  <div className="space-y-6 md:space-y-8">
                    <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 md:p-6">
                      <p className="text-muted-foreground italic">Content will be available soon.</p>
                    </div>
                  </div>

                  <div className="mt-6 text-center">
                    <Button 
                      onClick={() => setStep4Open(true)}
                      className="bg-primary hover:bg-primary/90 text-primary-foreground"
                    >
                      Go to Step 4
                    </Button>
                  </div>

                  <div className="h-16"></div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Step 4 */}
            <Dialog open={step4Open} onOpenChange={setStep4Open}>
              <DialogTrigger asChild>
                <div className="group cursor-pointer">
                  <div className="bg-card border border-border rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-primary/50 min-h-[160px] flex flex-col justify-between">
                    <div>
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 mb-4">
                        <span className="text-xl font-bold text-primary">4</span>
                      </div>
                      <h3 className="font-rockwell text-lg font-semibold text-foreground mb-3 leading-tight">
                        Creating Your Explanatory Statement for DS-11
                      </h3>
                    </div>
                    <Button variant="outline" size="sm" className="w-full mt-4 group-hover:border-primary/50 transition-colors">
                      Start Step 4
                    </Button>
                  </div>
                </div>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] md:max-h-[85vh] overflow-y-auto mx-2 md:mx-8 my-2 md:my-4 w-[95vw] md:w-auto [&>button]:hidden">
                <DialogHeader className="relative pb-4 border-b border-border/20">
                  <DialogClose asChild>
                    <Button
                      variant="outline"
                      className="absolute right-0 top-0 h-12 w-20 rounded-lg bg-background/95 hover:bg-primary/5 hover:text-primary transition-all duration-300 hover:scale-[1.02] border-2 border-border/40 hover:border-primary/40 shadow-sm hover:shadow-md backdrop-blur-sm group"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1.5 group-hover:-translate-x-0.5 transition-transform duration-200" />
                      <span className="text-sm font-semibold">Back</span>
                    </Button>
                  </DialogClose>
                  <DialogTitle className="text-lg md:text-2xl font-bold text-primary leading-tight pr-24">
                    Step 4: Creating Your Explanatory Statement for DS-11
                  </DialogTitle>
                </DialogHeader>
                <div className="text-foreground mt-4 md:mt-6 text-sm md:text-base">
                  <div className="space-y-6 md:space-y-8">
                    <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 md:p-6">
                      <p className="text-muted-foreground italic">Content will be available soon.</p>
                    </div>
                  </div>

                  <div className="mt-6 text-center">
                    <Button 
                      onClick={() => setStep5Open(true)}
                      className="bg-primary hover:bg-primary/90 text-primary-foreground"
                    >
                      Go to Step 5
                    </Button>
                  </div>

                  <div className="h-16"></div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Step 5 */}
            <Dialog open={step5Open} onOpenChange={setStep5Open}>
              <DialogTrigger asChild>
                <div className="group cursor-pointer">
                  <div className="bg-card border border-border rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-primary/50 min-h-[160px] flex flex-col justify-between">
                    <div>
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 mb-4">
                        <span className="text-xl font-bold text-primary">5</span>
                      </div>
                      <h3 className="font-rockwell text-lg font-semibold text-foreground mb-3 leading-tight">
                        Submitting the DS-11 Passport Application
                      </h3>
                    </div>
                    <Button variant="outline" size="sm" className="w-full mt-4 group-hover:border-primary/50 transition-colors">
                      Start Step 5
                    </Button>
                  </div>
                </div>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] md:max-h-[85vh] overflow-y-auto mx-2 md:mx-8 my-2 md:my-4 w-[95vw] md:w-auto [&>button]:hidden">
                <DialogHeader className="relative pb-4 border-b border-border/20">
                  <DialogClose asChild>
                    <Button
                      variant="outline"
                      className="absolute right-0 top-0 h-12 w-20 rounded-lg bg-background/95 hover:bg-primary/5 hover:text-primary transition-all duration-300 hover:scale-[1.02] border-2 border-border/40 hover:border-primary/40 shadow-sm hover:shadow-md backdrop-blur-sm group"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1.5 group-hover:-translate-x-0.5 transition-transform duration-200" />
                      <span className="text-sm font-semibold">Back</span>
                    </Button>
                  </DialogClose>
                  <DialogTitle className="text-lg md:text-2xl font-bold text-primary leading-tight pr-24">
                    Step 5: Submitting the DS-11 Passport Application
                  </DialogTitle>
                </DialogHeader>
                <div className="text-foreground mt-4 md:mt-6 text-sm md:text-base">
                  <div className="space-y-6 md:space-y-8">
                    <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 md:p-6">
                      <p className="text-muted-foreground italic">Content will be available soon.</p>
                    </div>
                  </div>

                  <div className="mt-6 text-center">
                    <Button 
                      onClick={() => navigate('/diy-processes')}
                      className="bg-primary hover:bg-primary/90 text-primary-foreground"
                    >
                      Complete Process
                    </Button>
                  </div>

                  <div className="h-16"></div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default StatusCorrectionPassport;